import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";

const ACCENT_TINTS = {
    cyan: {
        border: "rgba(0,219,255,0.18)",
        ring: "var(--tca-cyan-core)",
        glow: "0 0 24px rgba(0,219,255,0.28)",
        text: "text-tca-cyan",
        hi: "text-tca-cyan-hi",
    },
    "cyan-strong": {
        border: "rgba(0,219,255,0.40)",
        ring: "var(--tca-cyan-hi)",
        glow: "0 0 38px rgba(0,219,255,0.45)",
        text: "text-tca-cyan-hi",
        hi: "text-white",
    },
    gold: {
        border: "rgba(201,168,106,0.32)",
        ring: "var(--tca-gold-warm)",
        glow: "0 0 28px rgba(224,192,137,0.30)",
        text: "text-tca-gold-warm",
        hi: "text-tca-gold-warm",
    },
};

export default function PhaseCard({ phase, status, completionPct, onOpen, index }) {
    const t = ACCENT_TINTS[phase.accent] || ACCENT_TINTS.cyan;
    const isActive = status === "active";
    const isComplete = status === "complete";
    const isDim = status === "dim";

    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: isDim ? 0.55 : 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 * index }}
            whileHover={{ scale: isDim ? 1 : 1.025 }}
            onClick={onOpen}
            data-testid={`phase-card-${phase.id}`}
            data-status={status}
            className="group relative text-left w-full"
        >
            <HudFrame
                bracketColor={t.ring}
                bracketSize={isActive ? 16 : 12}
                borderTint={t.border}
                className={`
                    relative bg-tca-bg-panel/65 backdrop-blur-md
                    transition-all duration-500
                    ${isActive ? "shadow-[inset_0_0_40px_rgba(0,219,255,0.10)]" : ""}
                `}
                innerClassName="p-6 sm:p-7 min-h-[200px] flex flex-col"
            >
                {/* Boot pulse on active */}
                {isActive && (
                    <motion.div
                        aria-hidden="true"
                        animate={{ opacity: [0.2, 0.45, 0.2] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                        className="pointer-events-none absolute inset-0"
                        style={{ boxShadow: `inset 0 0 32px rgba(0,219,255,0.18)` }}
                    />
                )}

                <div className="relative z-10 flex items-start justify-between gap-4">
                    <div>
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-ink-muted/70">
                            // Phase · 0{index + 1}
                        </div>
                        <div className={`mt-1.5 font-display text-3xl tracking-[-0.018em] ${t.hi}`}>
                            {phase.name}
                        </div>
                    </div>

                    {/* Completion ring */}
                    <CompletionRing pct={completionPct} accent={phase.accent} />
                </div>

                <p className="relative z-10 mt-4 text-sm text-tca-ink-muted leading-snug flex-1">
                    {phase.description}
                </p>

                <div className="relative z-10 mt-5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex gap-1.5">
                        {phase.letters.map((L) => (
                            <span
                                key={L}
                                className={`
                                    inline-flex items-center justify-center w-7 h-7
                                    font-mono-tight text-[11px] font-semibold border
                                    ${isDim
                                        ? "border-tca-ink-dim/40 text-tca-ink-muted/40"
                                        : "border-tca-cyan/45 text-tca-cyan"}
                                `}
                            >
                                {L}
                            </span>
                        ))}
                    </div>

                    <span className={`inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-[0.22em] ${t.text}`}>
                        {isComplete ? (
                            <><Check size={12} /> Complete</>
                        ) : (
                            <>Open Workspace <ArrowRight size={11} className="transition-transform group-hover:translate-x-0.5" /></>
                        )}
                    </span>
                </div>
            </HudFrame>
        </motion.button>
    );
}

function CompletionRing({ pct, accent }) {
    const stroke = accent === "gold" ? "var(--tca-gold-warm)" : "var(--tca-cyan-core)";
    const dim = "rgba(255,255,255,0.08)";
    const r = 22, c = 2 * Math.PI * r;
    const off = c - (pct / 100) * c;
    return (
        <div className="relative w-14 h-14 shrink-0">
            <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
                <circle cx="28" cy="28" r={r} fill="none" stroke={dim} strokeWidth="2.5" />
                <circle
                    cx="28" cy="28" r={r}
                    fill="none" stroke={stroke} strokeWidth="2.5"
                    strokeDasharray={c}
                    strokeDashoffset={off}
                    style={{ transition: "stroke-dashoffset 0.6s ease" }}
                    strokeLinecap="round"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-mono-tight text-[11px] text-tca-cyan-hi tracking-tight">
                {pct}%
            </div>
        </div>
    );
}
